import { memo, useMemo } from 'react'
import { LoadingState } from '@highlight/ui'
import { ReminderCell } from '../../reminders/reminders-cell'
import { ReminderInput } from '../../reminders/reminders-input'
import { orderActiveRemindersForDisplay } from '../../../views/reminders-view/reminders-view-order'
import { useReminderActions, type ReminderItem } from '../../../hooks/use-reminders'
import { useRemindersData } from '../../../reminders-context'
import { UnreadBadge } from '../../unread-badge'
import { SidepanelSectionViewMore } from '../nested/section-view-more'
import { useHomeSectionLayout } from '../use-home-section-layout'
import { absorbRemainingItems, useProgressiveSectionLimit } from '../use-progressive-section-limit'
import { useIsSidepanelSectionCollapsed } from '../../../atoms'
import { useRemindersSurfacePresence } from '../../../hooks/reminder-linger'

const LOADING_MESSAGES = ['Loading reminders…', 'Getting things ready…', 'Almost there…']

export function SidepanelRemindersSection() {
  const { activeItems, isLoading } = useRemindersData()
  // Drops resolved reminders, so it holds just-completed ones open while it's
  // on screen (`reminder-linger`).
  useRemindersSurfacePresence()
  const { addItem } = useReminderActions()
  const { prioritySectionInitialItemLimit } = useHomeSectionLayout()
  const isCollapsed = useIsSidepanelSectionCollapsed('reminders')
  const { visibleCount, showMore } = useProgressiveSectionLimit(
    'reminders',
    prioritySectionInitialItemLimit,
    isCollapsed,
  )
  const orderedItems = useMemo(() => orderActiveRemindersForDisplay(activeItems), [activeItems])
  const visibleItemCount = absorbRemainingItems(visibleCount, orderedItems.length)
  const displayedItems = orderedItems.slice(0, visibleItemCount)
  const canShowMore = visibleItemCount < orderedItems.length

  if (isLoading) {
    return (
      <LoadingState className="w-full px-3 pb-3 pt-1">
        <LoadingState.Spinner />
        <LoadingState.Text messages={LOADING_MESSAGES} />
      </LoadingState>
    )
  }

  return (
    <div className="flex flex-col gap-1.5">
      <ReminderInput onSubmit={addItem} />
      <div className="flex flex-col gap-0.5">
        <ReminderList items={displayedItems} />
        {canShowMore && <SidepanelSectionViewMore onClick={showMore} />}
      </div>
    </div>
  )
}

const ReminderList = memo(function ReminderList({ items }: { items: Array<ReminderItem> }) {
  return (
    <>
      {items.map((item) => (
        <ReminderCell key={item.id} item={item} variant="row" />
      ))}
    </>
  )
})

export const RemindersBadge = memo(function RemindersBadge() {
  const { overdueCount } = useRemindersData()
  return <UnreadBadge count={overdueCount} />
})
